/**
 * useLongPolling Composable
 *
 * Alternative transport to useWebSocket for receiving real-time
 * transcription status updates via HTTP long polling.
 *
 * Activated by GrowthBook feature flag "NOA_long_polling".
 * Uses the same V1 payload format and callback interface as useWebSocket.
 */

import { ref, onUnmounted } from "vue";
import type { StatusChangeCallback, TranscriptionStatusPayload, TranscriptionStatus } from "@/composables/useWebSocket";
import { API_BASE_URL } from "@/utils/apiBaseUrl";

// =============================================================================
// Types
// =============================================================================

export interface LongPollingOptions {
  onError?: (error: Error) => void;
  onConnected?: () => void;
}

// =============================================================================
// Configuration
// =============================================================================

function getApiBaseUrl(): string {
  // Absolute base derived from the plugin script origin - a page-relative
  // base would resolve against the host PMS origin, whose edge routing
  // does not forward /api/sessions/*. See src/utils/apiBaseUrl.ts.
  return API_BASE_URL;
}

const MAX_BACKOFF_MS = 30000;
const INITIAL_BACKOFF_MS = 1000;
const MAX_CONSECUTIVE_ERRORS = 5;

// =============================================================================
// Main Composable
// =============================================================================

export function useLongPolling(
  onStatusChange: StatusChangeCallback,
  options?: LongPollingOptions,
) {
  const isPolling = ref(false);
  const connectionError = ref<string | null>(null);
  const connectionAttemptFailed = ref(false);

  let abortController: AbortController | null = null;
  let currentSessionId: string | null = null;
  let lastEventId: string | null = null;
  let consecutiveErrors = 0;
  let backoffMs = INITIAL_BACKOFF_MS;
  let backoffTimeout: ReturnType<typeof setTimeout> | null = null;
  let authToken: string | null = null;

  const statusChangeCallback = ref<StatusChangeCallback>(onStatusChange);

  const updateStatusChangeCallback = (newCallback: StatusChangeCallback) => {
    statusChangeCallback.value = newCallback;
  };

  function setAuthToken(token: string) {
    authToken = token;
  }

  async function pollOnce(): Promise<void> {
    if (!currentSessionId || !abortController) return;

    const baseUrl = getApiBaseUrl();
    const url = lastEventId
      ? `${baseUrl}/sessions/${currentSessionId}/poll?lastEventId=${encodeURIComponent(lastEventId)}`
      : `${baseUrl}/sessions/${currentSessionId}/poll`;

    const headers: Record<string, string> = {};
    if (authToken) {
      headers["x-access-token"] = authToken;
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        headers,
        signal: abortController.signal,
      });

      if (response.status === 200) {
        consecutiveErrors = 0;
        backoffMs = INITIAL_BACKOFF_MS;
        connectionError.value = null;

        const data = await response.json();

        if (data && data.type === "webhook" && data.event === "transcription_status_changed") {
          const payload: TranscriptionStatusPayload = {
            status: data.status as TranscriptionStatus,
            transcription: data.data?.transcription,
            summary: data.data?.summary,
            parsedSummary: data.data?.parsedSummary,
            reviewedSummary: data.data?.reviewedSummary,
            error: data.data?.error,
            doctorId: data.doctorId,
            facilityId: data.facilityId,
            episodeId: data.episodeId,
          };

          statusChangeCallback.value(data.status, payload);
        }

        if (data?.eventId) {
          lastEventId = data.eventId;
        }
      } else if (response.status === 204) {
        consecutiveErrors = 0;
        backoffMs = INITIAL_BACKOFF_MS;
        connectionError.value = null;
      } else if (response.status === 401) {
        connectionError.value = "Unauthorized";
        connectionAttemptFailed.value = true;
        stopPolling();
        return;
      } else {
        throw new Error(`Poll failed with status ${response.status}`);
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }

      consecutiveErrors++;
      connectionError.value = error instanceof Error ? error.message : "Network error";

      if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
        connectionAttemptFailed.value = true;
        options?.onError?.(error instanceof Error ? error : new Error("Max retries exceeded"));
      }

      await new Promise<void>((resolve) => {
        backoffTimeout = setTimeout(resolve, backoffMs);
      });
      backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
      return;
    }
  }

  async function pollLoop(): Promise<void> {
    while (isPolling.value && abortController && !abortController.signal.aborted) {
      await pollOnce();
      if (connectionAttemptFailed.value) break;
    }
  }

  function connect(sessionId?: string) {
    const sessionIdToUse = sessionId || currentSessionId;
    if (!sessionIdToUse) return;

    if (isPolling.value && currentSessionId === sessionIdToUse) return;

    stopPolling();

    currentSessionId = sessionIdToUse;
    lastEventId = null;
    consecutiveErrors = 0;
    backoffMs = INITIAL_BACKOFF_MS;
    connectionError.value = null;
    connectionAttemptFailed.value = false;

    abortController = new AbortController();
    isPolling.value = true;
    options?.onConnected?.();

    pollLoop();
  }

  function stopPolling() {
    isPolling.value = false;
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    if (backoffTimeout) {
      clearTimeout(backoffTimeout);
      backoffTimeout = null;
    }
  }

  function disconnect() {
    stopPolling();
    currentSessionId = null;
    lastEventId = null;
    connectionAttemptFailed.value = false;
    connectionError.value = null;
  }

  onUnmounted(() => {
    disconnect();
  });

  return {
    isConnected: isPolling,
    connectionAttemptFailed,
    connectionError,
    connect,
    disconnect,
    updateStatusChangeCallback,
    setAuthToken,
  };
}
